import ComponentCircleLoader from 'components/Loaders/ComponentCircleLoader';
import { t } from 'i18next';
import { useEffect } from 'react';
import styled from 'styled-components';
import { PageTitle } from 'styledComponents/common';

// ─── Layout ────────────────────────────────────────────────────────────────────

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
`;

const SectionCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const SectionAccent = styled.div`
  width: 5px;
  height: 26px;
  border-radius: 3px;
  background: ${({ color }) => color || '#2563eb'};
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #2f394e;
  margin: 0;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: ${({ bg }) => bg || '#f0fdf4'};
  border: 1.5px solid ${({ border }) => border || '#bbf7d0'};
  border-radius: 12px;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 140px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 14px ${({ shadow }) => shadow || 'rgba(25,172,101,0.18)'};
  }
`;

const CardValue = styled.div`
  font-size: 40px;
  font-weight: 700;
  color: ${({ color }) => color || '#16a34a'};
  line-height: 1.1;
`;

const CardLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #4b5563;
  margin-top: 8px;
`;

// ─── Card configs per section ───────────────────────────────────────────────────

const SECTIONS = [
  {
    key: 'service_center',
    titleKey: 'service_center_attendance',
    accentColor: '#7c3aed',
  },
    {
    key: 'committee',
    titleKey: 'committees_attendance',
    accentColor: '#d97706',
  },
   {
    key: 'building',
    titleKey: 'building_attendance',
    accentColor: '#16a34a',
  },
  {
    key: 'guard',
    titleKey: 'guards_attendance',
    accentColor: '#2563eb',
  }
 ];

const CARDS = [
  {
    dataKey: 'total_employees',
    labelKey: 'total_employees',
    bg: '#eff6ff',
    border: '#93c5fd',
    shadow: 'rgba(59,130,246,0.18)',
    valueColor: '#2563eb',
  },
  {
    dataKey: 'online_employees',
    labelKey: 'working_today',
    bg: '#f0fdf4',
    border: '#86efac',
    shadow: 'rgba(25,172,101,0.18)',
    valueColor: '#16a34a',
  },
  {
    dataKey: 'offline_employees',
    labelKey: 'not_working_today',
    bg: '#fff7ed',
    border: '#fcd34d',
    shadow: 'rgba(245,158,11,0.18)',
    valueColor: '#d97706',
  },
];

// ─── Component ─────────────────────────────────────────────────────────────────

const AttendanceDashboard = ({ getAttendanceDashboardStatus, isLoading, officeTypeStatus }) => {
  useEffect(() => {
    getAttendanceDashboardStatus();
  }, []);

  return (
    <div className="content">
      <PageHeader>
        <PageTitle style={{ marginBottom: 0 }}>{t('attendance_dashboard')}</PageTitle>
      </PageHeader>

      {isLoading ? (
        <ComponentCircleLoader padding="10vh" />
      ) : (
        SECTIONS.map(section => {
          const sectionData = officeTypeStatus ? officeTypeStatus[section.key] : null;
          return (
            <SectionCard key={section.key}>
              <SectionHeader>
                <SectionAccent color={section.accentColor} />
                <SectionTitle>{t(section.titleKey)}</SectionTitle>
              </SectionHeader>
              <CardsGrid>
                {CARDS.map(card => (
                  <StatCard
                    key={card.dataKey}
                    bg={card.bg}
                    border={card.border}
                    shadow={card.shadow}
                  >
                    <CardValue color={card.valueColor}>
                      {sectionData != null ? (sectionData[card.dataKey] ?? '-') : '-'}
                    </CardValue>
                    <CardLabel>{t(card.labelKey)}</CardLabel>
                  </StatCard>
                ))}
              </CardsGrid>
            </SectionCard>
          );
        })
      )}
    </div>
  );
};

export default AttendanceDashboard;
